import { Route } from "vue-router";
import { Dictionary, VueRouter } from "vue-router/types/router";

import { LogService, RouterService } from "@/services";
import store, { ApplicationModule, getStoreModule, SessionModule } from "@/store";
import {
  LookupTableItemKeys,
  LookupTablesKeys,
  ProfileView,
  SearchOrder,
  SearchQuery,
  SearchQueryStrings,
  VignetteHelpers,
  VignetteRoutes,
} from "@/types";

/**
 * Defines Search support functions
 *
 */
export class SearchService {
  private static _initialized = false;
  private static applicationStore: ApplicationModule;
  private static sessionStore: SessionModule;

  static _initialize(): void {
    if (this._initialized) {
      LogService.error("SearchService is already initialized.");
      return;
    }
    LogService.debug("Initializing SearchService.");

    this.applicationStore = getStoreModule(ApplicationModule, store);
    this.sessionStore = getStoreModule(SessionModule, store);

    this._initialized = true;
    LogService.info("SearchService is initialized.");
  }

  // Compares current SearchQuery page number with last SearchQuery from ApplicationStore
  static isSearchPageChanged(current: SearchQuery, previous: SearchQuery): boolean {
    return previous.page !== current.page;
  }

  // Compares current SearchQuery search view with last SearchQuery from ApplicationStore
  static isSearchViewChanged(current: SearchQuery, previous: SearchQuery): boolean {
    return previous.view !== current.view;
  }

  // Compares current SearchQuery page size with last SearchQuery from ApplicationStore
  static isPageSizeChanged(current: SearchQuery): boolean {
    const previous: SearchQuery | null = this.applicationStore.getSearchLastQuery;
    return this._isPageSizeChanged(current, previous);
  }

  // Compares current SearchQuery search order with last SearchQuery from ApplicationStore
  static isSearchOrderChanged(current: SearchQuery): boolean {
    const lastQuery: SearchQuery | null = this.applicationStore.getSearchLastQuery;
    return this._isSearchOrderChanged(current, lastQuery);
  }

  // Compares current SearchQuery config with last one from ApplicationStore
  static isSearchConfigChanged(currentSearchQuery: SearchQuery): boolean {
    const lastQuery: SearchQuery | null = this.applicationStore.getSearchLastQuery;
    return (
      lastQuery == null ||
      this.isSearchPageChanged(currentSearchQuery, lastQuery) ||
      this.isSearchViewChanged(currentSearchQuery, lastQuery) ||
      this._isSearchOrderChanged(currentSearchQuery, lastQuery) ||
      this._isPageSizeChanged(currentSearchQuery, lastQuery)
    );
  }

  // Compares current SearchQuery with last one from ApplicationStore
  static isSearchQueryChanged(currentSearchQuery: SearchQuery): boolean {
    const _lastQuery: SearchQuery | null = this.applicationStore.getSearchLastQuery;
    const lastQuery: SearchQueryStrings | null = _lastQuery ? this.generateSearchQueryStrings(_lastQuery) : null;
    const currentQuery: SearchQueryStrings = this.generateSearchQueryStrings(currentSearchQuery);
    return this.isSearchConfigChanged(currentSearchQuery) || this._isSearchQueryChanged(currentQuery, lastQuery);
  }

  static isSearchQueryEmpty(currentSearchQuery: SearchQuery): boolean {
    return this._isSearchQueryEmpty(this.generateSearchQueryStrings(currentSearchQuery));
  }

  static readSearchQueriesFromRoute($route: Route): SearchQuery {
    const searchQuery: SearchQuery = {
      // Search configuration
      page: VignetteHelpers.readQueryNum($route, "page", 1),
      pageSize: VignetteHelpers.readQueryNum($route, "pageSize", 12),
      order:
        VignetteHelpers.readQueryStr($route, "order", SearchOrder.DEFAULT) === SearchOrder.ALPHABETICAL
          ? SearchOrder.ALPHABETICAL
          : VignetteHelpers.readQueryStr($route, "order", SearchOrder.DEFAULT) === SearchOrder.RELEVANCE
          ? SearchOrder.RELEVANCE
          : SearchOrder.DEFAULT,
      view:
        VignetteHelpers.readQueryStr($route, "view", ProfileView.DEFAULT) === ProfileView.LIST
          ? ProfileView.LIST
          : VignetteHelpers.readQueryStr($route, "view", ProfileView.DEFAULT) === ProfileView.CARD
          ? ProfileView.CARD
          : ProfileView.DEFAULT,
      hidePayWalledProfiles: VignetteHelpers.readQueryTruthy($route, "hide_paywalled_profiles"),

      // Local variables
      autoCompleteKeyword: "",
      advancedSearchOpen: VignetteHelpers.readQueryTruthy($route, "advanced") || false,

      // Vignette simple search
      keyword: VignetteHelpers.readQueryStr($route, "q", ""),

      // Vignette advanced search
      currentOfficeCongressionalDistrict: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.STATES,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "cod", "")
      ),
      currentOfficeStates: [],
      politicalParty: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.POLITICAL_PARTIES,
        LookupTableItemKeys.NAME,
        VignetteHelpers.readQueryStr($route, "pp", "")
      ),
      religion: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.RELIGIONS,
        LookupTableItemKeys.NAME,
        VignetteHelpers.readQueryStr($route, "r", "")
      ),
      ethnicity: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.ETHNICITIES,
        LookupTableItemKeys.NAME,
        VignetteHelpers.readQueryStr($route, "et", "")
      ),
      gender: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.GENDERS,
        LookupTableItemKeys.NAME,
        VignetteHelpers.readQueryStr($route, "g", "")
      ),
      state: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.STATES,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "s", "")
      ),
      country: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.COUNTRIES,
        LookupTableItemKeys.NAME,
        VignetteHelpers.readQueryStr($route, "c", "")
      ),
      editorialGroup: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.EDITORIAL_GROUPS,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "eg", "")
      ),
      profileTypes: [],
      keyPolicyAreas: [],
      industries: [],
      committees: [],
      caucuses: [],
      agencies: [],
      city: VignetteHelpers.readQueryStr($route, "ct", ""),
      education: VignetteHelpers.readQueryStr($route, "ed", ""),
      includeFormer: VignetteHelpers.readQueryStr($route, "if", "false") === "true",
      // Birthdate
      birthYear: { id: "", name: "" },
      age: { id: "", name: "" },
      birthDateOperator: { id: "exact", name: "Born On" },
      // Special Interest Groups
      sig: this.applicationStore.getNullableLookupTableEntryByType(
        LookupTablesKeys.SPECIAL_INTEREST_GROUPS_FLAT,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "sig", "")
      ),
      sigOperator: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.SIG_OPERATOR,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "sigo", "gte")
      ),
      sigRating: this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.SIG_RATING_OPTIONS,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "sigr", "50")
      ),
    };

    if (VignetteHelpers.readQueryStr($route, "co", "") !== "") {
      searchQuery.currentOfficeStates = VignetteHelpers.readQueryStr($route, "co", "")
        .split(",")
        .map((state) =>
          this.applicationStore.getLookupTableEntryByType(LookupTablesKeys.STATES, LookupTableItemKeys.ID, state)
        );
    }
    if (VignetteHelpers.readQueryStr($route, "pt", "") !== "") {
      searchQuery.profileTypes = VignetteHelpers.readQueryStr($route, "pt", "")
        .split(",")
        .map((entryId) =>
          this.applicationStore.getLookupTableEntryByType(
            LookupTablesKeys.PROFILE_TYPES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (VignetteHelpers.readQueryStr($route, "kpa", "") !== "") {
      searchQuery.keyPolicyAreas = VignetteHelpers.readQueryStr($route, "kpa", "")
        .split(",")
        .map((entryId) =>
          this.applicationStore.getLookupTableEntryByType(
            LookupTablesKeys.KEY_POLICY_AREAS_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (VignetteHelpers.readQueryStr($route, "ind", "") !== "") {
      searchQuery.industries = VignetteHelpers.readQueryStr($route, "ind", "")
        .split(",")
        .map((entryId) =>
          this.applicationStore.getLookupTableEntryByType(
            LookupTablesKeys.INDUSTRIES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (VignetteHelpers.readQueryStr($route, "com", "") !== "") {
      searchQuery.committees = VignetteHelpers.readQueryStr($route, "com", "")
        .split(",")
        .map((entryId) =>
          this.applicationStore.getLookupTableEntryByType(
            LookupTablesKeys.COMMITTEES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (VignetteHelpers.readQueryStr($route, "ca", "") !== "") {
      searchQuery.caucuses = VignetteHelpers.readQueryStr($route, "ca", "")
        .split(",")
        .map((entryId) =>
          this.applicationStore.getLookupTableEntryByType(
            LookupTablesKeys.CAUCUSES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (VignetteHelpers.readQueryStr($route, "ag", "") !== "") {
      searchQuery.agencies = VignetteHelpers.readQueryStr($route, "ag", "")
        .split(",")
        .map((entryId) =>
          this.applicationStore.getLookupTableEntryByType(
            LookupTablesKeys.AGENCIES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    // Initialize Birthdate - either birth year or age
    if (VignetteHelpers.readQueryStr($route, "b", "") !== "") {
      searchQuery.birthYear = this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.BIRTH_YEAR_OPTIONS,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "b", "")
      );
      searchQuery.birthDateOperator = this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.BIRTH_YEAR_OPERATOR,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "bo", "exact")
      );
    } else if (VignetteHelpers.readQueryStr($route, "a", "") !== "") {
      searchQuery.age = this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.AGE_OPTIONS,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "a", "")
      );
      searchQuery.birthDateOperator = this.applicationStore.getLookupTableEntryByType(
        LookupTablesKeys.AGE_OPERATOR,
        LookupTableItemKeys.ID,
        VignetteHelpers.readQueryStr($route, "bo", "exact")
      );
    }

    return searchQuery;
  }

  static generateSearchQueryStrings(query: SearchQuery): SearchQueryStrings {
    return {
      // keyword search query and search configurations
      q: query.keyword,
      pageSize: query.pageSize,
      page: query.page,
      order: query.order,
      view: query.view,

      // user preferences
      hidePayWalledProfiles: query.hidePayWalledProfiles,

      // advanced search queries
      current_office_congressional_district: query.currentOfficeCongressionalDistrict.id,
      gender: query.gender.name,
      political_party: query.politicalParty.name,
      religion: query.religion.name,
      ethnicity: query.ethnicity.name,
      current_office_state_abbr: query.currentOfficeStates.map((state) => state.id).join(","),
      city: query.city,
      state: query.state.id,
      country: query.country.name,
      education: query.education,
      editorial_group: query.editorialGroup.id,
      profile_types: query.profileTypes.map((profileTypes) => profileTypes.id).join(","),
      key_policy_areas: query.keyPolicyAreas.map((keyPolicyArea) => keyPolicyArea.id).join(","),
      industry: query.industries.map((industry) => industry.id).join(","),
      include_former: query.includeFormer,
      committees: query.committees.map((committee) => committee.id).join(","),
      caucuses: query.caucuses.map((caucus) => caucus.id).join(","),
      agency: query.agencies.map((agency) => agency.id).join(","),

      sig_id: !!query.sig && query.sig.id ? query.sig.id : "",
      sig_operator: !!query.sig && query.sig.id ? query.sigOperator.id : "",
      sig_rating: !!query.sig && query.sig.id ? query.sigRating.id : "",

      // UI selections - mapped to birth_year
      birth_year_selection: query.birthYear.id,
      age_selection: query.age.id,
      birth_date_operator: query.birthDateOperator.id,
    };
  }

  static generateRouteQuery($route: Route, searchQuery: SearchQuery): Dictionary<string | (string | null)[]> {
    const _routeQuery: Dictionary<string | (string | null)[]> = Object.assign({}, $route.query);
    const _query = this.generateSearchQueryStrings(searchQuery);

    if (_query.q) _routeQuery.q = _query.q;
    else delete _routeQuery.q;
    // Only keep the page number if greater than 1
    if (_query.page > 1) _routeQuery.page = String(_query.page);
    else delete _routeQuery.page;
    // Only keep the page size if it is not the default (12)
    if (_query.pageSize !== 12) _routeQuery.pageSize = String(_query.pageSize);
    else delete _routeQuery.pageSize;
    if (_query.order === SearchOrder.ALPHABETICAL) _routeQuery.order = _query.order;
    else delete _routeQuery.order;
    if (_query.view === ProfileView.LIST) _routeQuery.view = _query.view;
    else delete _routeQuery.view;
    if (_query.gender) _routeQuery.g = _query.gender;
    else delete _routeQuery.g;
    if (_query.political_party) _routeQuery.pp = _query.political_party;
    else delete _routeQuery.pp;
    if (_query.religion) _routeQuery.r = _query.religion;
    else delete _routeQuery.r;
    if (_query.ethnicity) _routeQuery.et = _query.ethnicity;
    else delete _routeQuery.et;
    if (_query.current_office_state_abbr) _routeQuery.co = _query.current_office_state_abbr;
    else delete _routeQuery.co;
    if (_query.city) _routeQuery.ct = _query.city;
    else delete _routeQuery.ct;
    if (_query.state) _routeQuery.s = _query.state;
    else delete _routeQuery.s;
    if (_query.country) _routeQuery.c = _query.country;
    else delete _routeQuery.c;
    if (_query.education) _routeQuery.ed = _query.education;
    else delete _routeQuery.ed;
    if (_query.birth_year_selection) _routeQuery.b = _query.birth_year_selection;
    else delete _routeQuery.b;
    if (_query.age_selection) _routeQuery.a = String(_query.age_selection);
    else delete _routeQuery.a;
    if (_query.birth_year_selection || _query.age_selection) _routeQuery.bo = _query.birth_date_operator;
    else delete _routeQuery.bo;
    if (_query.editorial_group) _routeQuery.eg = _query.editorial_group;
    else delete _routeQuery.eg;
    if (_query.profile_types) _routeQuery.pt = _query.profile_types;
    else delete _routeQuery.pt;
    if (_query.key_policy_areas) _routeQuery.kpa = _query.key_policy_areas;
    else delete _routeQuery.kpa;
    if (_query.industry) _routeQuery.ind = _query.industry;
    else delete _routeQuery.ind;
    if (_query.include_former) _routeQuery.if = String(_query.include_former);
    else delete _routeQuery.if;
    if (_query.committees) _routeQuery.com = _query.committees;
    else delete _routeQuery.com;
    if (_query.caucuses) _routeQuery.ca = _query.caucuses;
    else delete _routeQuery.ca;
    if (_query.agency) _routeQuery.ag = _query.agency;
    else delete _routeQuery.ag;
    if (_query.sig_id) {
      _routeQuery.sig = String(_query.sig_id);
      _routeQuery.sigo = String(_query.sig_operator);
      _routeQuery.sigr = String(_query.sig_rating);
    } else {
      delete _routeQuery.sig;
      delete _routeQuery.sigo;
      delete _routeQuery.sigr;
    }
    // TODO: it will require input of the form "NY-12" for a congressional district.
    // if (_query.current_office_congressional_district)
    //   query.cod = _query.current_office_congressional_district;
    // else delete query.cod;

    // Do not expand advanced search section after initial load.
    delete _routeQuery.advanced;

    if (_query.hidePayWalledProfiles === true) _routeQuery.hide_paywalled_profiles = "true";
    else delete _routeQuery.hide_paywalled_profiles;

    return _routeQuery;
  }

  // When last query is used, most likely means search was changed using search chips
  static search($router: VueRouter, $route: Route, useLastQuery = false): void {
    let currentSearchQuery: SearchQuery = this.applicationStore.getSearchCurrentQuery;

    if (useLastQuery) {
      const lastSearchQuery: SearchQuery | null = this.applicationStore.getSearchLastQuery;
      if (lastSearchQuery == null) {
        LogService.warn("SearchService.search: called with useLastQuery flag and null lastSearchQuery.");
        return;
      }
      // We can just proceed with the search, assuming we just removed a filter on SearchResults page and page is valid
      currentSearchQuery = lastSearchQuery;
    } else {
      if (!SearchService.isSearchQueryChanged(currentSearchQuery)) {
        if (currentSearchQuery.page === 1) {
          // Do not do anything if search is clicked without changing anything.
          LogService.warn("SearchService.search: avoided redundant navigation to current location.");
          return;
        }
        LogService.warn("SearchService.search: updating same search to first page.");
        // If search is clicked without changing any criteria, go to first page to start over.
        currentSearchQuery.page = 1;
      } else {
        if (
          this.applicationStore.getSearchLastQuery != null &&
          (SearchService.isSearchOrderChanged(currentSearchQuery) ||
            SearchService.isPageSizeChanged(currentSearchQuery))
        ) {
          currentSearchQuery.page = 1;
        } else if (SearchService.isSearchConfigChanged(currentSearchQuery)) {
          this.applicationStore.setSearchKeepCachedResults(true);
        } else {
          currentSearchQuery.page = 1;
          this.applicationStore.setSearchKeepCachedResults(false);
        }
      }
    }

    // Ready to search
    this._search($router, $route, currentSearchQuery);
  }

  private static _search($router: VueRouter, $route: Route, searchQuery: SearchQuery): void {
    RouterService.navigateByName($router, VignetteRoutes.Search, SearchService.generateRouteQuery($route, searchQuery));
  }

  // Compares current SearchQuery search order with last SearchQuery from ApplicationStore
  private static _isSearchOrderChanged(current: SearchQuery, previous: SearchQuery | null): boolean {
    return (
      previous == null ||
      (previous.order !== current.order &&
        (previous.order === SearchOrder.ALPHABETICAL || current.order === SearchOrder.ALPHABETICAL))
    );
  }

  // Compares current SearchQuery page size with last SearchQuery from ApplicationStore
  private static _isPageSizeChanged(current: SearchQuery, previous: SearchQuery | null): boolean {
    return previous == null || previous.pageSize !== current.pageSize;
  }

  private static _isSearchQueryEmpty(query: SearchQueryStrings): boolean {
    return !(
      query.q ||
      query.order !== SearchOrder.DEFAULT ||
      query.editorial_group ||
      query.profile_types ||
      query.key_policy_areas ||
      query.industry ||
      query.include_former ||
      query.committees ||
      query.caucuses ||
      query.agency ||
      query.current_office_congressional_district ||
      query.gender ||
      query.political_party ||
      query.religion ||
      query.ethnicity ||
      query.current_office_state_abbr ||
      query.city ||
      query.state ||
      query.country ||
      query.education ||
      query.birth_year_selection ||
      query.age_selection ||
      query.sig_id
    );
  }

  private static _isSearchQueryChanged(current: SearchQueryStrings, previous: SearchQueryStrings | null): boolean {
    return (
      previous == null ||
      previous.q !== current.q ||
      previous.editorial_group !== current.editorial_group ||
      previous.profile_types !== current.profile_types ||
      previous.key_policy_areas !== current.key_policy_areas ||
      previous.industry !== current.industry ||
      previous.include_former !== current.include_former ||
      previous.committees !== current.committees ||
      previous.caucuses !== current.caucuses ||
      previous.agency !== current.agency ||
      previous.current_office_congressional_district !== current.current_office_congressional_district ||
      previous.gender !== current.gender ||
      previous.political_party !== current.political_party ||
      previous.religion !== current.religion ||
      previous.ethnicity !== current.ethnicity ||
      previous.current_office_state_abbr !== current.current_office_state_abbr ||
      previous.city !== current.city ||
      previous.state !== current.state ||
      previous.country !== current.country ||
      previous.education !== current.education ||
      previous.birth_year_selection !== current.birth_year_selection ||
      previous.age_selection !== current.age_selection ||
      previous.birth_date_operator !== current.birth_date_operator ||
      previous.sig_id !== current.sig_id ||
      previous.sig_operator !== current.sig_operator ||
      previous.sig_rating !== current.sig_rating
    );
  }
}
